/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * What the agent needs to know about themselves and their queues.
 *
 * The console had none of this. An agent could not see their own presence
 * without going to find the dock, could not tell whether their device had
 * actually registered, and had no idea whether anyone was waiting. The last
 * one matters most: an agent who cannot see a queue depth has no way to tell
 * "quiet morning" from "my softphone silently deregistered twenty minutes
 * ago", and those look identical from an empty screen.
 *
 * Queue counts are polled rather than pushed. The websocket already has
 * `agent_presence` and `queue_stats` handlers, but nothing on the server ever
 * publishes either, so subscribing would have produced a strip that was
 * permanently empty and looked broken.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Card, Chip, Tooltip } from "@mui/material";
import { HeadsetMic, MicOff, WifiTethering, WifiTetheringOff } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import { useAuth } from "auth/Auth";
import { PresenceControl } from "telephony/components";

const cc = colors.callCenter;

const REFRESH_MS = 10000;

export default function AgentStrip({
  presence,
  onPresenceChange,
  registered,
  isLeader,
  micError,
  refreshToken,
}) {
  const { axiosInstance } = useAuth();
  const [queues, setQueues] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/call-center/queues", {
        params: { with_stats: true },
      });
      setQueues(Array.isArray(res.data) ? res.data.filter((q) => q.is_active) : []);
    } catch {
      // A failed stats poll must never take the console down; the agent's own
      // controls on the left are what they actually need to take a call.
      setQueues([]);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load, refreshToken]);

  const waiting = queues.reduce((sum, q) => sum + (q.waiting_calls || 0), 0);
  const staffed = queues.reduce((sum, q) => sum + (q.available_agents || 0), 0);

  return (
    <Card
      sx={{
        p: 2,
        mb: 3,
        border: `1px solid ${cc.cardBorder}`,
        boxShadow: "none",
        borderRadius: "12px",
      }}
    >
      <MDBox
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={{ xs: 1.5, md: 3 }}
      >
        <MDBox display="flex" alignItems="center" gap={1.5}>
          <HeadsetMic sx={{ color: cc.muted }} />
          <PresenceControl
            presence={presence}
            onChange={onPresenceChange}
            disabled={!isLeader}
          />
        </MDBox>

        <MDBox display="flex" alignItems="center" gap={1}>
          <Tooltip
            title={
              registered
                ? "Your softphone is registered with Twilio and can receive calls."
                : "Your softphone is not registered. Calls will not reach you, whatever your presence says."
            }
          >
            <MDBox display="flex" alignItems="center" gap={0.5}>
              {registered ? (
                <WifiTethering sx={{ fontSize: 18, color: colors.success.main }} />
              ) : (
                <WifiTetheringOff sx={{ fontSize: 18, color: cc.danger }} />
              )}
              <MDTypography variant="caption" color="text">
                {registered ? "Device ready" : "Device not registered"}
              </MDTypography>
            </MDBox>
          </Tooltip>

          {micError ? (
            <Tooltip title={micError.message || "Microphone unavailable."}>
              <MicOff sx={{ fontSize: 18, color: cc.danger }} />
            </Tooltip>
          ) : null}
        </MDBox>

        <MDBox flexGrow={1} />

        {/* Queue depth is the number an agent actually acts on: it is the
            difference between a quiet line and a broken one. */}
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Tooltip title="Callers waiting across your queues.">
            <Chip
              size="small"
              color={waiting > 0 ? "warning" : "default"}
              variant={waiting > 0 ? "filled" : "outlined"}
              label={`${waiting} waiting`}
            />
          </Tooltip>
          <Tooltip title="Agents available and registered across your queues.">
            <Chip size="small" variant="outlined" label={`${staffed} available`} />
          </Tooltip>
          {queues.slice(0, 4).map((q) => (
            <Tooltip
              key={q.id}
              title={`${q.name}: ${q.waiting_calls || 0} waiting, ${
                q.available_agents || 0
              } of ${q.member_count || 0} agents available`}
            >
              <Chip
                size="small"
                variant="outlined"
                label={`${q.name} ${q.waiting_calls || 0}`}
                sx={{
                  borderColor:
                    q.waiting_calls > 0 && q.available_agents === 0
                      ? cc.danger
                      : cc.cardBorder,
                }}
              />
            </Tooltip>
          ))}
        </MDBox>
      </MDBox>

      {!isLeader ? (
        <MDTypography variant="caption" color="text" display="block" mt={1}>
          The softphone is active in another browser tab, so calls will ring there.
        </MDTypography>
      ) : null}
    </Card>
  );
}
